/* iter-72h — Soft-expiry landing screen.
 *
 * Full-page takeover shown when the current user is on a trial whose
 * `trial_expires_at` is in the past. Rather than letting the /app
 * routes 401 into oblivion (which loses conversion), we greet the
 * customer with:
 *   • their saved projects — a visual reminder of what they'd lose
 *     if they walked away,
 *   • the The Loom benefits list (fetched from
 *     /api/subscriptions/plans, falls back to a curated list),
 *   • a clear "Pick a plan" primary CTA.
 *
 * Kept dumb — parent App is responsible for gating on
 * `user.trial_expired` from the /auth/me payload.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, Clock, Sparkles, LogOut, ShieldCheck, Feather, Loader2, PlayCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth_context';
import { setResumeIntent } from '@/lib/resumeIntent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_BENEFITS = [
  'Unlimited chapters & projects',
  'Character Bible + inline suggestions',
  'Adobe PDF + KDP bundle exports',
  'Priority Claude Sonnet 4.5 generations',
  'Community forums + real-time chat dock',
];

export default function TrialExpiredScreen() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [benefits, setBenefits] = useState(FALLBACK_BENEFITS);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/projects`, { withCredentials: true })
      .then((r) => { if (!cancelled) setProjects(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setProjects([]); });
    axios.get(`${API}/subscriptions/plans`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const pro = Array.isArray(r.data?.plans)
          ? r.data.plans.find((p) => p.id === 'pro' || p.id === 'professional' || p.name?.toLowerCase().includes('pro'))
          : null;
        if (pro && Array.isArray(pro.features) && pro.features.length > 0) {
          setBenefits(pro.features);
        }
      })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);

  const doSignOut = async () => {
    setSigningOut(true);
    try { await logout(); }
    catch (_e) { /* silent */ }
    finally {
      setSigningOut(false);
      toast.message('Signed out. Come back any time.');
      navigate('/login');
    }
  };

  return (
    <div
      data-testid="trial-expired-screen"
      className="min-h-screen w-full overflow-y-auto bg-background text-foreground"
    >
      <div className="mx-auto max-w-3xl px-6 py-14">
        <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-700 dark:text-amber-300">
          <Clock className="h-3.5 w-3.5" />
          Your 14-day trial has ended
        </div>

        <h1 className="text-3xl font-semibold leading-tight sm:text-4xl">
          Welcome back{user?.name ? `, ${user.name}` : ''} — pick a plan to keep writing.
        </h1>
        <p className="mt-3 max-w-xl text-[15px] text-muted-foreground">
          Your drafts, characters, and chapter bibles are safe. Upgrade any
          plan below to continue exactly where you left off — nothing is
          deleted, nothing is lost.
        </p>

        {/* Saved projects card — reminds them of the sunk investment ---- */}
        <section
          data-testid="trial-expired-projects"
          className="mt-8 rounded-lg border bg-secondary/40 p-4"
        >
          <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            <BookOpen className="h-3.5 w-3.5" />
            Waiting for you to come back
          </div>
          {projects === null && (
            <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading your projects…
            </div>
          )}
          {projects && projects.length === 0 && (
            <div className="text-[13px] text-muted-foreground">
              You didn&rsquo;t save any projects during the trial — but a Pro
              plan drops you straight into a fresh manuscript with the full
              suite of tools unlocked.
            </div>
          )}
          {projects && projects.length > 0 && (
            <ul className="space-y-2" data-testid="trial-expired-projects-list">
              {projects.slice(0, 6).map((p) => {
                const chapterCount = p.chapters_count ?? p.chapters?.length ?? 0;
                return (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-3 rounded-md bg-background px-3 py-2 text-[13.5px]"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-semibold">{p.title || 'Untitled'}</div>
                      <div className="truncate text-[11.5px] text-muted-foreground">
                        {chapterCount} chapter
                        {chapterCount === 1 ? '' : 's'}
                        {p.updated_at && ` · updated ${new Date(p.updated_at).toLocaleDateString()}`}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="shrink-0 gap-1.5 text-[11.5px]"
                      data-testid={`trial-expired-resume-${p.id}`}
                      onClick={() => {
                        /* iter-72i — persist the intent, then take them
                         * through the billing panel. AppShell picks it
                         * up the moment /auth/me flips trial_expired
                         * to false and jumps straight into the project.
                         */
                        setResumeIntent(p.id, null);
                        navigate('/app?view=billing');
                      }}
                      title="Store this project as your resume target and open the billing panel"
                    >
                      <PlayCircle className="h-3.5 w-3.5 text-primary" />
                      Resume &amp; upgrade
                    </Button>
                  </li>
                );
              })}
              {projects.length > 6 && (
                <li className="text-[11.5px] text-muted-foreground">
                  &nbsp;… plus {projects.length - 6} more.
                </li>
              )}
            </ul>
          )}
        </section>

        {/* Pro benefits ------------------------------------------------- */}
        <section className="mt-8 rounded-lg border bg-background p-4">
          <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            What you unlock with The Loom
          </div>
          <ul className="space-y-2" data-testid="trial-expired-benefits">
            {benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-[13.5px]">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Actions ------------------------------------------------------- */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Button
            size="lg"
            className="gap-2"
            data-testid="trial-expired-upgrade"
            onClick={() => navigate('/app?view=billing')}
          >
            <Sparkles className="h-4 w-4" />
            Pick a plan &amp; unlock everything
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="gap-2"
            data-testid="trial-expired-signout"
            disabled={signingOut}
            onClick={doSignOut}
          >
            {signingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
            Sign out for now
          </Button>
        </div>

        <p className="mt-8 flex items-center gap-2 text-[11.5px] text-muted-foreground">
          <Feather className="h-3 w-3" />
          Questions? Reach out at{' '}
          <a className="underline" href="mailto:info@crownnrose.com">info@crownnrose.com</a>.
        </p>
      </div>
    </div>
  );
}
